"use client";

import { Graphics } from "@/components/graphics";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";
import React from "react";
import { toast as sonnerToast } from "sonner";

interface ToastProps {
  id: string | number;
  title: string;
  description?: string;
  type?: "success" | "error" | "default" | "warning" | "info";
  button?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  titleClassName?: string;
  onlyIcon?: boolean;
  hideIconOnSm?: boolean;
}
interface CustomToast {
  (toast: Omit<ToastProps, "id" | "type">): string | number;
  success: (toast: Omit<ToastProps, "id" | "type">) => string | number;
  error: (toast: Omit<ToastProps, "id" | "type">) => string | number;
  warning: (toast: Omit<ToastProps, "id" | "type">) => string | number;
  info: (toast: Omit<ToastProps, "id" | "type">) => string | number;
}

const popupToastClassName = "!w-full !max-w-none !min-w-0 min-[384px]:!min-w-0";

const baseToast = (toast: Omit<ToastProps, "id">) => {
  return sonnerToast.custom((id) => (
    <Toast {...toast} id={id} className={cn(popupToastClassName, toast.className)} />
  ));
};

const toast = ((toastProps: Omit<ToastProps, "id" | "type">) => {
  return baseToast({ ...toastProps, type: "default" });
}) as CustomToast;

toast.success = (toastProps: Omit<ToastProps, "id" | "type">) => {
  return baseToast({ ...toastProps, type: "success" });
};

toast.error = (toastProps: Omit<ToastProps, "id" | "type">) => {
  return baseToast({ ...toastProps, type: "error" });
};
toast.warning = (toastProps: Omit<ToastProps, "id" | "type">) => {
  return baseToast({ ...toastProps, type: "warning" });
};
toast.info = (toastProps: Omit<ToastProps, "id" | "type">) => {
  return baseToast({ ...toastProps, type: "info" });
};

export function Toast(props: ToastProps) {
  const { title, description, button, id, type = "default", className, titleClassName, hideIconOnSm, onlyIcon } = props;

  const getBgColor = () => {
    switch (type) {
      case "success":
        return "bg-[#ECFDF5]";
      case "error":
        return "bg-[#FEF2F2]";
      case "warning":
        return "bg-[#FFFBEB]";
      case "info":
        return "bg-[#0093D012]";
      default:
        return "bg-white";
    }
  };

  return (
    <div
      className={cn(
        "font-cabinet-grotesk flex w-full max-w-[348px] flex-col items-center gap-3 rounded-sm px-3 py-4.5 shadow-sm ring-1 ring-black/5 min-[384px]:min-w-[348px]",
        getBgColor(),
        className
      )}
    >
      <div className="flex w-full flex-1 items-center justify-between">
        <div className="w-full">
          <div className={cn("flex items-center gap-3", description && "items-start")}>
            <div className={cn(description && "mt-0.5", hideIconOnSm && "max-sm:hidden")}>
              {type === "error" ? (
                <Graphics.errorIcon />
              ) : (
                <Info
                  size={15}
                  className={cn(
                    "flex-shrink-0",
                    type === "success" && "text-[#059669]",
                    type === "warning" && "text-[#D97706]",
                    type === "info" && "text-blue-brand"
                  )}
                />
              )}
            </div>
            {!onlyIcon && (
              <div className="min-w-0 flex-grow">
                <p
                  className={cn(
                    "text-sm",
                    type === "success" && "text-[#059669]",
                    type === "warning" && "text-[#D97706]",
                    type === "info" && "text-blue-brand",
                    type === "error" && "text-[#DC2626]",
                    titleClassName
                  )}
                >
                  {title}
                </p>
                {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
      {button && (
        <div className="w-full flex-shrink-0 rounded-md text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden">
          {button.label && (
            <button
              className="w-full cursor-pointer rounded bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-600 hover:bg-indigo-100"
              onClick={() => {
                button.onClick();
                sonnerToast.dismiss(id);
              }}
            >
              {button.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export { toast };
